// Libraries
import { ReactNode } from 'react';

// Hooks
import { useAppSelector } from '../../../app/hooks';

// Features
import { selectUser } from '../../../features/user/userSlice';
import {
    selectSession,
    selectSessionStatus,
    selectSessionError,
} from '../../../features/session/sessionSlice';

// Styles
import styles from './SessionScreen.module.css';
import StateHandler from '../../templates/StateScreen/StateHandler';

/**
 * @function SessionScreen
 * Provides a container for current game information
 *
 * @example
 * return (
 *   <SessionScreen>{children}</SessionScreen>
 * )
 */
function SessionScreen({ children }: SessionScreenProps): JSX.Element {
    const user = useAppSelector(selectUser);
    const session = useAppSelector(selectSession);
    const sessionStatus = useAppSelector(selectSessionStatus);
    const sessionError = useAppSelector(selectSessionError);

    if (session === null && sessionStatus !== 'idle') {
        return (
            <StateHandler
                status={sessionStatus}
                error={sessionError}
                loadingText="Loading session..."
            />
        );
    }

    return (
        <main
            className={`${styles.container}${
                user.chat_on ? ` ${styles.push}` : ''
            }`}
        >
            {children}
        </main>
    );
}

/**
 * @interface SessionScreenProps
 * Describes the properties required for the SessionScreen component
 *
 * @property {ReactElement} children List of game data elements
 */
export interface SessionScreenProps {
    children?: ReactNode;
}

// Exports
export default SessionScreen;
